import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Plus, Search, Edit, Trash2, Check, X, 
  Eye, EyeOff, Shield, User, Mail, Key, ToggleLeft, ToggleRight,
  Briefcase, ChevronDown
} from 'lucide-react';
import { getApiHeaders } from '../utils/apiHelpers';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Mansioni predefinite
const MANSIONI_PREDEFINITE = [
  { value: 'administrator', label: 'Administrator' },
  { value: 'chef', label: 'Chef' },
  { value: 'sous_chef', label: 'Sous Chef' },
  { value: 'aiuto_cuoco', label: 'Aiuto Cuoco' },
  { value: 'cameriere', label: 'Cameriere' },
  { value: 'barista', label: 'Barista' },
  { value: 'sommelier', label: 'Sommelier' },
  { value: 'responsabile_sala', label: 'Responsabile Sala' },
  { value: 'cassiere', label: 'Cassiere' },
  { value: 'magazziniere', label: 'Magazziniere' },
  { value: 'altro', label: 'Altro' }
];

const TeamPage = ({ embedded = false }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [permissionsSchema, setPermissionsSchema] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state separato per evitare re-render
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [email, setEmail] = useState('');
  const [mansione, setMansione] = useState('');
  const [permissions, setPermissions] = useState({
    dashboard: true,
    cassa: false,
    ordini: false,
    ddt: false,
    haccp: false,
    fb_cost: false,
    fatture: false,
    fornitori: false,
    team: false,
    locali: false
  });

  useEffect(() => {
    loadMembers();
    loadPermissionsSchema();
  }, []);

  const loadMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/team/members`, {
        headers: getApiHeaders()
      });
      setMembers(res.data.members || []);
    } catch (error) {
      console.error('Errore caricamento team:', error);
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per visualizzare il team');
      } else {
        
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPermissionsSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/team/permissions-schema`, {
        headers: getApiHeaders()
      });
      setPermissionsSchema(res.data.permissions || []);
    } catch (error) {
      console.error('Errore caricamento schema permessi:', error);
    }
  };

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setNomeCompleto('');
    setEmail('');
    setMansione('');
    setPermissions({
      dashboard: true,
      cassa: false,
      ordini: false,
      ddt: false,
      haccp: false,
      fb_cost: false,
      fatture: false,
      fornitori: false,
      team: false,
      locali: false
    });
    setShowPassword(false);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setUsername(member.username);
    setPassword('');
    setNomeCompleto(member.nome_completo || '');
    setEmail(member.email || '');
    setMansione(member.mansione || '');
    setPermissions(member.permissions || {
      dashboard: true,
      cassa: false,
      ordini: false,
      ddt: false,
      haccp: false,
      fb_cost: false,
      fatture: false,
      fornitori: false,
      team: false,
      locali: false
    });
    setShowEditModal(true);
  };

  const handleCreateMember = async () => {
    if (!username.trim()) {
      toast.error('Username obbligatorio');
      return;
    }
    if (!password.trim()) {
      toast.error('Password obbligatoria');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/team/members`, {
        username,
        password,
        nome_completo: nomeCompleto,
        email,
        mansione,
        permissions
      }, {
        headers: getApiHeaders()
      });
      toast.success('Membro creato con successo');
      setShowAddModal(false);
      resetForm();
      loadMembers();
    } catch (error) {
      console.error('Errore creazione membro:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        nome_completo: nomeCompleto,
        email,
        mansione,
        permissions
      };
      if (password.trim()) {
        updateData.password = password;
      }
      
      await axios.put(`${API}/team/members/${selectedMember.id}`, updateData, {
        headers: getApiHeaders()
      });
      toast.success('Membro aggiornato');
      setShowEditModal(false);
      setSelectedMember(null);
      loadMembers();
    } catch (error) {
      console.error('Errore aggiornamento:', error);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (member) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/team/members/${member.id}`, {
        attivo: !member.attivo
      }, {
        headers: getApiHeaders()
      });
      toast.success(member.attivo ? 'Membro disattivato' : 'Membro attivato');
      loadMembers();
    } catch (error) {
      toast.error('Errore nel cambio stato');
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Sei sicuro di voler eliminare ${member.username}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/team/members/${member.id}`, {
        headers: getApiHeaders()
      });
      toast.success('Membro eliminato');
      loadMembers();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const togglePermission = (key) => {
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredMembers = members.filter(m => 
    m.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.nome_completo || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.mansione || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const countActivePermissions = (perms) => {
    if (!perms) return 0;
    return Object.values(perms).filter(v => v === true).length;
  };

  const getMansioneLabel = (value) => {
    const found = MANSIONI_PREDEFINITE.find(m => m.value === value);
    return found ? found.label : value;
  };

  // Render modal content
  const renderModalContent = (isEdit) => (
    <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[60]">
      <div className="bg-transparent lg:bg-white rounded-t-3xl lg:rounded-2xl w-full lg:max-w-lg max-h-[85vh] lg:max-h-[90vh] overflow-hidden flex flex-col mb-16 lg:mb-0">
        <div className="p-4 border-b border-slate-700 lg:border-gray-100 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 lg:from-blue-50 lg:to-blue-50">
          <div>
            <h3 className="font-bold text-white lg:text-gray-900">
              {isEdit ? 'Modifica Membro' : 'Nuovo Membro Team'}
            </h3>
            <p className="text-xs text-blue-400 lg:text-blue-600">
              {isEdit ? 'Modifica permessi e dati' : 'Crea un nuovo accesso all\'app'}
            </p>
          </div>
          <button 
            onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}
            className="p-1.5 bg-slate-700 lg:bg-transparent hover:bg-slate-600 lg:hover:bg-blue-100 rounded-lg"
          >
            <X className="h-4 w-4 text-white lg:text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
              <User className="h-4 w-4 inline mr-1" />
              Username *
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isEdit}
              placeholder="es. marco.rossi"
              className="w-full px-3 py-2 rounded-lg border bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-white lg:text-gray-900 placeholder-slate-500 disabled:bg-slate-700 lg:disabled:bg-gray-100 disabled:text-slate-400"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
              <Key className="h-4 w-4 inline mr-1" />
              Password {isEdit ? '(lascia vuoto per non cambiare)' : '*'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? '••••••••' : 'Inserisci password'}
                className="w-full px-3 py-2 pr-10 rounded-lg border bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-white lg:text-gray-900 placeholder-slate-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 lg:text-gray-400 hover:text-white lg:hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Nome completo */}
          <div>
            <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">Nome Completo</label>
            <input
              type="text"
              value={nomeCompleto}
              onChange={(e) => setNomeCompleto(e.target.value)}
              placeholder="es. Marco Rossi"
              className="w-full px-3 py-2 rounded-lg border bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-white lg:text-gray-900 placeholder-slate-500"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
              <Mail className="h-4 w-4 inline mr-1" />
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="es. marco@ristorante.it"
              className="w-full px-3 py-2 rounded-lg border bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-white lg:text-gray-900 placeholder-slate-500"
            />
          </div>

          {/* Mansione - Dropdown */}
          <div>
            <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
              <Briefcase className="h-4 w-4 inline mr-1" />
              Mansione / Ruolo
            </label>
            <div className="relative">
              <select
                value={mansione}
                onChange={(e) => setMansione(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none text-white lg:text-gray-900"
              >
                <option value="">Seleziona mansione...</option>
                {MANSIONI_PREDEFINITE.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Permessi */}
          <div>
            <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-2">
              <Shield className="h-4 w-4 inline mr-1" />
              Permessi Sezioni
            </label>
            <div className="bg-slate-800 lg:bg-gray-50 rounded-xl p-3 space-y-2 max-h-48 overflow-y-auto border border-slate-700 lg:border-transparent">
              {permissionsSchema.map(perm => (
                <button
                  key={perm.key}
                  type="button"
                  onClick={() => togglePermission(perm.key)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    permissions[perm.key]
                      ? 'border-green-500/50 bg-green-500/10 lg:border-green-300 lg:bg-green-50'
                      : 'border-slate-600 lg:border-gray-200 bg-slate-700/50 lg:bg-white hover:border-slate-500 lg:hover:border-gray-300'
                  }`}
                >
                  <div className="text-left">
                    <p className={`font-medium text-sm ${permissions[perm.key] ? 'text-green-400 lg:text-green-700' : 'text-white lg:text-gray-700'}`}>
                      {perm.label}
                    </p>
                    <p className="text-xs text-slate-400 lg:text-gray-500">{perm.description}</p>
                  </div>
                  {permissions[perm.key] ? (
                    <ToggleRight className="h-6 w-6 text-green-400 lg:text-green-600 flex-shrink-0" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-slate-500 lg:text-gray-400 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer - sempre visibile sopra la bottom nav */}
        <div className="p-4 border-t border-slate-700 lg:border-gray-100 flex gap-3 bg-transparent lg:bg-white flex-shrink-0">
          <button
            type="button"
            onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}
            className="flex-1 py-2.5 bg-slate-800 lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium text-sm border border-slate-700 lg:border-transparent"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={isEdit ? handleUpdateMember : handleCreateMember}
            disabled={saving}
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Check className="h-4 w-4" />
                {isEdit ? 'Salva' : 'Crea'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 pt-16 lg:pt-6 lg:p-6 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg lg:text-2xl font-bold text-white lg:text-gray-900">Team</h1>
          <p className="text-xs lg:text-sm text-slate-400 lg:text-gray-500">Gestisci accessi e permessi</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuovo Membro
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
        <input
          type="text"
          placeholder="Cerca membri..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-slate-800 lg:bg-white border-slate-700 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-white lg:text-gray-900 placeholder-slate-400"
        />
      </div>

      {/* Members List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-slate-600 lg:text-gray-300 mx-auto mb-3" />
          <p className="text-slate-400 lg:text-gray-500">Nessun membro del team</p>
          <p className="text-sm text-slate-500 lg:text-gray-400 mt-1">Crea il primo membro per dare accesso all'app</p>
        </div>
      ) : (
        <div className="grid gap-3 lg:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredMembers.map(member => (
            <div
              key={member.id}
              className={`bg-slate-800/80 lg:bg-white rounded-xl border p-4 transition-all ${
                member.attivo ? 'border-slate-700/50 lg:border-gray-200' : 'border-red-500/30 lg:border-red-200 bg-red-500/10 lg:bg-red-50/30'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    member.attivo ? 'bg-blue-600/20 lg:bg-blue-100' : 'bg-slate-700 lg:bg-gray-200'
                  }`}>
                    <User className={`h-5 w-5 ${member.attivo ? 'text-blue-400 lg:text-blue-600' : 'text-slate-500 lg:text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-white lg:text-gray-900">{member.username}</p>
                    {member.nome_completo && (
                      <p className="text-xs text-slate-400 lg:text-gray-500">{member.nome_completo}</p>
                    )}
                    {member.mansione && (
                      <p className="text-xs text-blue-400 lg:text-blue-600 font-medium">{getMansioneLabel(member.mansione)}</p>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  member.attivo 
                    ? 'bg-green-500/20 text-green-400 lg:bg-green-100 lg:text-green-700' 
                    : 'bg-red-500/20 text-red-400 lg:bg-red-100 lg:text-red-700'
                }`}>
                  {member.attivo ? 'Attivo' : 'Disattivo'}
                </span>
              </div>

              {member.email && (
                <p className="text-xs text-slate-500 lg:text-gray-500 mb-3 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {member.email}
                </p>
              )}

              <div className="mb-3">
                <p className="text-xs text-slate-500 lg:text-gray-500 mb-1">Permessi attivi:</p>
                <div className="flex flex-wrap gap-1">
                  {permissionsSchema
                    .filter(p => member.permissions?.[p.key])
                    .slice(0, 4)
                    .map(p => (
                      <span key={p.key} className="px-2 py-0.5 bg-blue-600/20 text-blue-400 lg:bg-blue-100 lg:text-blue-700 rounded text-xs">
                        {p.label}
                      </span>
                    ))
                  }
                  {countActivePermissions(member.permissions) > 4 && (
                    <span className="px-2 py-0.5 bg-slate-700 text-slate-400 lg:bg-gray-100 lg:text-gray-600 rounded text-xs">
                      +{countActivePermissions(member.permissions) - 4}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-slate-700/50 lg:border-gray-100">
                <button
                  onClick={() => openEditModal(member)}
                  className="flex-1 py-2 bg-slate-700 lg:bg-gray-100 hover:bg-slate-600 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1"
                >
                  <Edit className="h-3 w-3" />
                  Modifica
                </button>
                <button
                  onClick={() => handleToggleActive(member)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 ${
                    member.attivo
                      ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 lg:bg-yellow-100 lg:hover:bg-yellow-200 lg:text-yellow-700'
                      : 'bg-green-500/20 hover:bg-green-500/30 text-green-400 lg:bg-green-100 lg:hover:bg-green-200 lg:text-green-700'
                  }`}
                >
                  {member.attivo ? (
                    <><ToggleLeft className="h-3 w-3" /> Disattiva</>
                  ) : (
                    <><ToggleRight className="h-3 w-3" /> Attiva</>
                  )}
                </button>
                <button
                  onClick={() => handleDeleteMember(member)}
                  className="p-2 bg-red-500/20 lg:bg-red-100 hover:bg-red-500/30 lg:hover:bg-red-200 text-red-400 lg:text-red-600 rounded-lg"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showAddModal && renderModalContent(false)}
      {showEditModal && renderModalContent(true)}
    </div>
  );
};

export default TeamPage;
